import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { supabase } from '../utils/supabaseClient';
import {
  ReportTrackerContext,
  type PendingReport,
  type ReportNotification,
  type ReportTrackerContextValue
} from './ReportTrackerContextBase';

const LOCAL_STORAGE_KEY = 'moonmanifest.pendingReports';
const NOTIFICATION_STORAGE_KEY = 'moonmanifest.reportNotifications';

function readFromStorage<T>(key: string, fallback: T): T {
  if (typeof window === 'undefined') {
    return fallback;
  }

  try {
    const raw = window.localStorage.getItem(key);
    if (!raw) return fallback;
    return JSON.parse(raw) as T;
  } catch (error) {
    console.error(`[ReportTracker] Failed to parse ${key}:`, error);
    return fallback;
  }
}

function writeToStorage<T>(key: string, value: T): void {
  if (typeof window === 'undefined') {
    return;
  }

  try {
    window.localStorage.setItem(key, JSON.stringify(value));
  } catch (error) {
    console.error(`[ReportTracker] Failed to write ${key}:`, error);
  }
}

export const ReportTrackerProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const location = useLocation();
  const [pendingReports, setPendingReports] = useState<PendingReport[]>(() =>
    readFromStorage<PendingReport[]>(LOCAL_STORAGE_KEY, [])
  );
  const [notifications, setNotifications] = useState<ReportNotification[]>(() =>
    readFromStorage<ReportNotification[]>(NOTIFICATION_STORAGE_KEY, [])
  );

  const pollingRef = useRef<NodeJS.Timeout | null>(null);
  const isFetchingRef = useRef(false);

  useEffect(() => {
    writeToStorage(LOCAL_STORAGE_KEY, pendingReports);
  }, [pendingReports]);

  useEffect(() => {
    writeToStorage(NOTIFICATION_STORAGE_KEY, notifications);
  }, [notifications]);

  const trackReports = useCallback((reports: PendingReport[]) => {
    if (!reports || reports.length === 0) {
      return;
    }

    setPendingReports(prev => {
      const existingIds = new Set(prev.map(item => item.id));
      const next = [...prev];

      reports.forEach(report => {
        if (!existingIds.has(report.id)) {
          next.push(report);
        }
      });

      return next;
    });
  }, []);

  const clearPending = useCallback((predicate: (report: PendingReport) => boolean) => {
    setPendingReports(prev => prev.filter(report => !predicate(report)));
  }, []);

  const acknowledgeNotification = useCallback((id: string) => {
    setNotifications(prev => prev.filter(note => note.id !== id));
  }, []);

  const markNotificationAnnounced = useCallback((id: string) => {
    setNotifications(prev => prev.map(note => note.id === id ? { ...note, announced: true } : note));
  }, []);

  const fetchStatuses = useCallback(async () => {
    if (isFetchingRef.current) {
      return;
    }
    if (pendingReports.length === 0) {
      return;
    }

    isFetchingRef.current = true;
    try {
      const profileIds = Array.from(new Set(pendingReports.map(report => report.profileId)));
      const sessionIds = Array.from(new Set(pendingReports.map(report => report.sessionId)));

      const { data, error } = await supabase
        .from('report_deliveries')
        .select('profile_id, report_type, generation_status, report_url, error_message, stripe_checkout_session_id')
        .in('profile_id', profileIds)
        .in('stripe_checkout_session_id', sessionIds);

      if (error) {
        console.error('[ReportTracker] Failed to fetch report status:', error);
        return;
      }

      if (!Array.isArray(data)) {
        return;
      }

      const updates: Array<{ entry: PendingReport; status: 'completed' | 'failed'; reportUrl?: string; errorMessage?: string }> = [];

      pendingReports.forEach(pending => {
        const match = data.find(row => row.profile_id === pending.profileId && row.report_type === pending.reportType);
        if (!match) {
          return;
        }

        if (match.generation_status === 'completed' && match.report_url) {
          updates.push({
            entry: pending,
            status: 'completed',
            reportUrl: match.report_url ?? undefined
          });
        } else if (match.generation_status === 'failed') {
          updates.push({
            entry: pending,
            status: 'failed',
            errorMessage: match.error_message ?? undefined
          });
        }
      });

      if (updates.length > 0) {
        setPendingReports(prev => prev.filter(report => !updates.some(update => update.entry.id === report.id)));

        setNotifications(prev => {
          const existingIds = new Set(prev.map(item => item.id));
          const next = [...prev];

          updates.forEach(update => {
            const notificationId = `${update.entry.id}:${update.status}`;
            if (existingIds.has(notificationId)) {
              return;
            }

            next.push({
              id: notificationId,
              profileId: update.entry.profileId,
              sessionId: update.entry.sessionId,
              reportType: update.entry.reportType,
              status: update.status,
              reportUrl: update.reportUrl,
              errorMessage: update.errorMessage,
              token: update.entry.token,
              createdAt: new Date().toISOString()
            });
          });

          return next;
        });
      }
    } finally {
      isFetchingRef.current = false;
    }
  }, [pendingReports]);

  useEffect(() => {
    if (pollingRef.current) {
      clearInterval(pollingRef.current);
      pollingRef.current = null;
    }

    // OPTIMIZATION: Only poll for report status when on the report library page
    // This prevents continuous Supabase queries from blocking "networkidle" on other pages
    // Reports will still be tracked in localStorage and checked when user visits /report-library
    const isOnReportLibraryPage = location.pathname === '/report-library' || location.pathname === '/report-status';

    if (pendingReports.length === 0 || !isOnReportLibraryPage) {
      return;
    }

    fetchStatuses();
    pollingRef.current = setInterval(fetchStatuses, 5000);

    return () => {
      if (pollingRef.current) {
        clearInterval(pollingRef.current);
        pollingRef.current = null;
      }
    };
  }, [pendingReports, fetchStatuses, location.pathname]);

  const contextValue = useMemo<ReportTrackerContextValue>(() => ({
    pendingReports,
    notifications,
    trackReports,
    clearPending,
    acknowledgeNotification,
    markNotificationAnnounced
  }), [pendingReports, notifications, trackReports, clearPending, acknowledgeNotification, markNotificationAnnounced]);

  return (
    <ReportTrackerContext.Provider value={contextValue}>
      {children}
    </ReportTrackerContext.Provider>
  );
};
